// Cờ non-interactive của `avatar ai setup` (T1) — khai báo, kiểm sớm, và đọc bí mật.
//
// VÌ SAO TÁCH FILE: `ai.ts` là nơi đăng ký 4 lệnh con, không nên phình vì 6 cờ
// của riêng một lệnh. Tách ra còn cho phép test kiểm hợp đồng cờ mà không phải
// dựng cả cây commander.
//
// ⚠️ TRẬT TỰ KIỂM LÀ MỘT PHẦN CỦA HỢP ĐỒNG: `assertAiSetupFlagsComplete` phải
// chạy TRƯỚC mọi việc nặng/gây tác dụng phụ (detect + `npm i -g` Claude Code,
// gọi mạng /v1/models). Kiểm muộn thì ca âm "thiếu cờ" phải cài Claude Code rồi
// mới báo lỗi — chậm, bẩn máy, và không chạy được trong CI.

import type { Command } from "commander";
import { requireFlag } from "./non-interactive-context.js";
import { readSecretIfRequested } from "./read-secret-from-stdin.js";

/** Provider hợp lệ. `use-global` không có cờ phụ nào vì nó chép nguyên config global. */
export const AI_PROVIDER_VALUES = ["subscription", "llmlite", "anthropic", "use-global"] as const;

export type AiProviderFlagValue = (typeof AI_PROVIDER_VALUES)[number];

/** Giá trị 6 cờ, đúng tên camelCase mà commander sinh ra từ tên cờ dài. */
export interface AiSetupFlags {
  aiProvider?: string;
  anthropicModel?: string;
  anthropicApiKeyStdin?: boolean;
  llmliteBaseUrl?: string;
  llmliteModel?: string;
  llmliteApiKeyStdin?: boolean;
}

/** Bí mật đã đọc từ stdin — tách khỏi `AiSetupFlags` để không lẫn vào log/JSON. */
export interface AiSetupSecrets {
  anthropicApiKey?: string;
  llmliteApiKey?: string;
}

const COMMAND = "ai setup";

/** Đăng ký 6 cờ lên `ai setup`. Cờ bí mật là boolean — giá trị đi qua stdin, không qua argv. */
export function registerAiSetupFlags(cmd: Command): Command {
  return cmd
    .option(`--ai-provider <${AI_PROVIDER_VALUES.join("|")}>`, "Chọn AI provider, không hỏi")
    .option("--anthropic-model <id>", "Model Anthropic dùng mặc định")
    .option("--anthropic-api-key-stdin", "Đọc Anthropic API key từ stdin (không qua argv)")
    .option("--llmlite-base-url <url>", "Base URL gateway LLMLite")
    .option("--llmlite-model <id>", "Model LLMLite dùng mặc định")
    .option("--llmlite-api-key-stdin", "Đọc LLMLite API key từ stdin (không qua argv)");
}

/**
 * Kiểm đủ cờ cho provider đã chọn — ném `MissingFlagError` ở cờ ĐẦU TIÊN còn thiếu.
 *
 * Chỉ gọi khi đang ở chế độ non-interactive; có TTY thì thiếu cờ là chuyện bình
 * thường, wizard sẽ hỏi.
 */
export function assertAiSetupFlagsComplete(flags: AiSetupFlags): AiProviderFlagValue {
  const provider = requireFlag(flags.aiProvider, {
    flag: "--ai-provider",
    command: COMMAND,
    hint: `--ai-provider ${AI_PROVIDER_VALUES.join("|")}`,
  });

  if (!(AI_PROVIDER_VALUES as readonly string[]).includes(provider)) {
    throw new Error(
      `--ai-provider "${provider}" không hợp lệ. Chọn một trong: ${AI_PROVIDER_VALUES.join(", ")}`,
    );
  }

  if (provider === "anthropic") {
    requireFlag(flags.anthropicModel, {
      flag: "--anthropic-model",
      command: COMMAND,
      hint: "--anthropic-model claude-sonnet-4-5",
    });
    requireFlag(flags.anthropicApiKeyStdin, {
      flag: "--anthropic-api-key-stdin",
      command: COMMAND,
      hint: 'echo "$KEY" | avatar ai setup --non-interactive --anthropic-api-key-stdin …',
    });
  }

  if (provider === "llmlite") {
    requireFlag(flags.llmliteBaseUrl, {
      flag: "--llmlite-base-url",
      command: COMMAND,
      hint: "--llmlite-base-url https://ai.nal.vn",
    });
    requireFlag(flags.llmliteModel, {
      flag: "--llmlite-model",
      command: COMMAND,
      hint: "--llmlite-model nal-claude-sonnet",
    });
    requireFlag(flags.llmliteApiKeyStdin, {
      flag: "--llmlite-api-key-stdin",
      command: COMMAND,
      hint: 'echo "$KEY" | avatar ai setup --non-interactive --llmlite-api-key-stdin …',
    });
  }

  return provider as AiProviderFlagValue;
}

/**
 * Đọc bí mật của provider đang chọn từ stdin.
 *
 * Chỉ đọc ĐÚNG một bí mật: stdin là dòng dùng một lần, đọc hai lần thì lần sau
 * luôn rỗng — mà lỗi đó im lặng (key rỗng → "key không hợp lệ" ở tầng dưới).
 */
export async function readAiSetupSecrets(flags: AiSetupFlags): Promise<AiSetupSecrets> {
  if (flags.anthropicApiKeyStdin === true) {
    return { anthropicApiKey: await readSecretIfRequested(true) };
  }
  if (flags.llmliteApiKeyStdin === true) {
    return { llmliteApiKey: await readSecretIfRequested(true) };
  }
  return {};
}
